import { FC } from "react";
import toast from "react-hot-toast";

import { Modal } from "shared/ui";
import CalendarForm from "shared/ui/calendar-form";

import Router from "@/helpers/router";

import { describeTopTenFailure } from "../lib/errors";
import { useAddToTopTen } from "../data/queries";

export const ADD_TO_TOP_TEN_MODAL_ID = "add-to-top-ten-modal";

interface AddToTopTenModalPropsI {
  campaignId?: string;
  accountId?: string;
  /** The Top 10 already holds its limit: explain instead of asking. */
  isFull?: boolean;
  limit: number;
  onSuccess?: () => void;
}

const FullTopTen: FC<{ limit: number; onClose?: () => void }> = ({
  limit,
  onClose,
}) => (
  <div className="flex flex-col gap-4">
    <p className="typography-body-4">
      All {limit} spots are taken. Remove one from your Top 10 in Open pipeline,
      then add this lead.
    </p>

    <div className="flex justify-end gap-2">
      <button type="button" className="btn-ae-text" onClick={onClose}>
        Cancel
      </button>
      <a href={Router.accounts()} className="btn-ae-default">
        Open pipeline
      </a>
    </div>
  </div>
);

const AddToTopTenForm: FC<
  Omit<AddToTopTenModalPropsI, "isFull" | "limit"> & { onClose?: () => void }
> = ({ campaignId, accountId, onSuccess, onClose }) => {
  const { mutateAsync, isPending } = useAddToTopTen();

  // CalendarForm hands over the chosen instant (ISO) first, then its parts.
  const handleSubmit = async (
    followUpTime: string,
    _time: string,
    _timezone: string,
    notes: string
  ) => {
    if (!campaignId || !accountId) {
      return;
    }

    try {
      await mutateAsync({
        campaignId,
        accountId,
        follow_up_time: followUpTime,
        notes: notes || undefined,
      });
    } catch (error) {
      toast.error(describeTopTenFailure(error, "Failed to add to your Top 10"));

      return;
    }

    toast.success("Added to your Top 10");
    onSuccess?.();
    onClose?.();
  };

  return (
    <CalendarForm
      bookingDeadlineInMonths={false}
      cancelButton={{ type: "button", text: "Cancel", onClick: onClose }}
      successButton={{ type: "button", text: "Add to Top 10" }}
      onFormSubmit={handleSubmit}
      isLoading={isPending}
      isNotes
      notesLabel="Notes (optional)"
    />
  );
};

/**
 * Adds a lead to the caller's Top 10 (C-16 S4): the call-by time becomes the
 * lead's callback, and the bell rings when it comes due.
 */
export const AddToTopTenModal: FC<AddToTopTenModalPropsI> = ({
  isFull,
  limit,
  ...props
}) => (
  <Modal
    id={ADD_TO_TOP_TEN_MODAL_ID}
    showCloseButton
    unmountOnClose
    title="Add to Top 10"
    description={
      isFull
        ? "Your Top 10 is full."
        : "Pick when you'll call next. We'll remind you when it's due."
    }
  >
    {({ handleClose }) =>
      isFull ? (
        <FullTopTen limit={limit} onClose={handleClose} />
      ) : (
        <AddToTopTenForm {...props} onClose={handleClose} />
      )
    }
  </Modal>
);
